import { useState } from "react";
import api from "../../services/api";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import "./ProfitReport.css";

export default function ProfitReport() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGenerateReport = async () => {
    if (!fromDate || !toDate) {
      return Swal.fire("Notice", "You must select both dates", "warning");
    }

    if (new Date(fromDate) > new Date(toDate)) {
      return Swal.fire("Error", "The start date cannot be later than the end date", "error");
    }

    setLoading(true);
    try {
      const response = await api.get("/reports/profit", {
        params: { from: fromDate, to: toDate },
      });

      setReportData(response.data);

      Swal.fire({
        icon: "success",
        title: "Report Generated",
        text: "The profit report was generated successfully",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error generating report:", error);
      Swal.fire("Error", "Could not generate the profit report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setReportData(null);
  };

  const handleExportExcel = () => {
    if (!reportData) return;

    const workbook = XLSX.utils.book_new();

    // ==========================================
    // SHEET 1: PROFIT BY DAY
    // ==========================================

    const dataByDay = [
      ["PROFIT BY DAY"],
      ["Period:", `${reportData.periodo.desde} to ${reportData.periodo.hasta}`],
      [],
      ["DATE", "TOTAL SOLD", "TOTAL COST", "PROFIT", "MARGIN %"],
    ];

    if (reportData.por_dia && reportData.por_dia.length > 0) {
      reportData.por_dia.forEach((day) => {
        dataByDay.push([
          day.fecha,
          `$${day.total_vendido.toFixed(2)}`,
          `$${day.costo_total.toFixed(2)}`,
          `$${day.ganancia.toFixed(2)}`,
          `${day.margen_porcentaje.toFixed(2)}%`,
        ]);
      });

      dataByDay.push([]);
      dataByDay.push([
        "TOTALS",
        `$${reportData.resumen.total_vendido.toFixed(2)}`,
        `$${reportData.resumen.costo_total.toFixed(2)}`,
        `$${reportData.resumen.ganancia_total.toFixed(2)}`,
        `${reportData.resumen.margen_promedio.toFixed(2)}%`,
      ]);
    } else {
      dataByDay.push([
        "ENTIRE PERIOD",
        `$${reportData.resumen.total_vendido.toFixed(2)}`,
        `$${reportData.resumen.costo_total.toFixed(2)}`,
        `$${reportData.resumen.ganancia_total.toFixed(2)}`,
        `${reportData.resumen.margen_promedio.toFixed(2)}%`,
      ]);
    }

    const wsByDay = XLSX.utils.aoa_to_sheet(dataByDay);
    XLSX.utils.book_append_sheet(workbook, wsByDay, "Profit by Day");

    // ==========================================
    // SHEET 2: PROFIT BY PRODUCT
    // ==========================================

    const dataByProduct = [
      ["PROFIT BY PRODUCT"],
      ["Period:", `${reportData.periodo.desde} to ${reportData.periodo.hasta}`],
      [],
      ["DATE", "CODE", "NAME", "PRESENTATION", "TYPE", "QTY", "BASE UNITS", "UNIT COST", "UNIT PRICE", "TOTAL COST", "TOTAL SALE", "PROFIT", "MARGIN %"],
    ];

    reportData.detalle.forEach((item) => {
      dataByProduct.push([
        item.fecha || "-",
        item.codigo,
        item.nombre,
        item.presentacion,
        item.tipo,
        item.cantidad_vendida,
        item.unidades_base,
        item.precio_compra_unitario ? `$${item.precio_compra_unitario.toFixed(2)}` : "-",
        `$${item.precio_venta.toFixed(2)}`,
        `$${item.costo_total.toFixed(2)}`,
        `$${item.venta_total.toFixed(2)}`,
        `$${item.ganancia_neta.toFixed(2)}`,
        `${item.margen_porcentaje.toFixed(2)}%`,
      ]);
    });

    dataByProduct.push([]);
    dataByProduct.push([
      "TOTALS",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      `$${reportData.resumen.costo_total.toFixed(2)}`,
      `$${reportData.resumen.total_vendido.toFixed(2)}`,
      `$${reportData.resumen.ganancia_total.toFixed(2)}`,
      `${reportData.resumen.margen_promedio.toFixed(2)}%`,
    ]);

    const wsByProduct = XLSX.utils.aoa_to_sheet(dataByProduct);
    XLSX.utils.book_append_sheet(workbook, wsByProduct, "Profit by Product");

    // ==========================================
    // SHEET 3: GENERAL SUMMARY
    // ==========================================
    const summaryData = [
      ["PROFIT REPORT - SUMMARY"],
      ["Period:", `${reportData.periodo.desde} to ${reportData.periodo.hasta}`],
      [],
      ["GENERAL SUMMARY"],
      ["Total Sold", `$${reportData.resumen.total_vendido.toFixed(2)}`],
      ["Total Cost", `$${reportData.resumen.costo_total.toFixed(2)}`],
      ["Total Profit", `$${reportData.resumen.ganancia_total.toFixed(2)}`],
      ["Average Margin", `${reportData.resumen.margen_promedio.toFixed(2)}%`],
      [],
      ["BREAKDOWN BY TYPE"],
      ["Products - Sold", `$${reportData.desglose.productos.total_vendido.toFixed(2)}`],
      ["Products - Profit", `$${reportData.desglose.productos.ganancia.toFixed(2)}`],
      ["Services - Sold", `$${reportData.desglose.servicios.total_vendido.toFixed(2)}`],
      ["Services - Profit", `$${reportData.desglose.servicios.ganancia.toFixed(2)}`],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, wsSummary, "Summary");

    const fileName = `Profit_Report_${fromDate}_to_${toDate}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    Swal.fire({
      icon: "success",
      title: "✅ Excel Downloaded",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p><strong>3 sheets were generated:</strong></p>
          <ul>
            <li>📅 <strong>Profit by Day:</strong> Daily summary</li>
            <li>📦 <strong>Profit by Product:</strong> Full detail</li>
            <li>📊 <strong>Summary:</strong> Consolidated view</li>
          </ul>
        </div>
      `,
      confirmButtonText: "Got it",
      confirmButtonColor: "#4CAF50",
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const getPieData = () => {
    if (!reportData) return [];
    return [
      {
        name: "Product Profit",
        value: reportData.desglose.productos.ganancia,
        color: "#0088FE",
      },
      {
        name: "Service Profit",
        value: reportData.desglose.servicios.ganancia,
        color: "#00C49F",
      },
    ];
  };

  const COLORS = ["#0088FE", "#00C49F"];

  return (
    <div className="profit-report-container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">💰 Profit Report</h2>
        </div>

        <div className="card-body">
          <div className="filters-section">
            <div className="filters-row">
              <div className="form-group">
                <label className="form-label">📅 Start Date</label>
                <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>

              <div className="form-group">
                <label className="form-label">📅 End Date</label>
                <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>

              <div className="filters-actions">
                <button className="btn btn-primary" onClick={handleGenerateReport} disabled={loading}>
                  {loading ? "⏳ Generating..." : "🔍 Generate Report"}
                </button>
                <button className="btn btn-secondary" onClick={handleClear}>
                  🔄 Clear
                </button>
              </div>
            </div>
          </div>

          {loading && (
            <div className="text-center mt-4">
              <div className="spinner"></div>
              <p className="mt-2">Generating profit report...</p>
            </div>
          )}

          {reportData && !loading && (
            <div className="report-results">
              <div className="export-section">
                <button className="btn btn-success" onClick={handleExportExcel}>
                  📊 Export to Excel
                </button>
              </div>

              <div className="summary-section">
                <h3 className="section-title">📈 General Summary</h3>
                <div className="kpi-cards">
                  <div className="kpi-card kpi-success">
                    <div className="kpi-icon">💵</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Total Sold</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.total_vendido)}</div>
                    </div>
                  </div>

                  <div className="kpi-card kpi-danger">
                    <div className="kpi-icon">📉</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Total Cost</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.costo_total)}</div>
                    </div>
                  </div>

                  <div className="kpi-card kpi-primary">
                    <div className="kpi-icon">💰</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Total Profit</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.ganancia_total)}</div>
                    </div>
                  </div>

                  <div className="kpi-card kpi-info">
                    <div className="kpi-icon">📊</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Average Margin</div>
                      <div className="kpi-value">{reportData.resumen.margen_promedio.toFixed(2)}%</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="breakdown-section">
                <h3 className="section-title">📦 Breakdown: Products vs Services</h3>

                <div className="breakdown-grid">
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={getPieData()}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {getPieData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="breakdown-cards">
                    <div className="breakdown-card">
                      <h4>📦 Products</h4>
                      <div className="breakdown-item">
                        <span>Total Sold:</span>
                        <strong>{formatCurrency(reportData.desglose.productos.total_vendido)}</strong>
                      </div>
                      <div className="breakdown-item">
                        <span>Profit:</span>
                        <strong className="text-success">{formatCurrency(reportData.desglose.productos.ganancia)}</strong>
                      </div>
                    </div>

                    <div className="breakdown-card">
                      <h4>🔧 Services</h4>
                      <div className="breakdown-item">
                        <span>Total Sold:</span>
                        <strong>{formatCurrency(reportData.desglose.servicios.total_vendido)}</strong>
                      </div>
                      <div className="breakdown-item">
                        <span>Profit:</span>
                        <strong className="text-success">{formatCurrency(reportData.desglose.servicios.ganancia)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3 className="section-title">📋 Item Detail</h3>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Presentation</th>
                        <th>Type</th>
                        <th>Qty</th>
                        <th>Base Units</th>
                        <th>Unit Cost</th>
                        <th>Unit Price</th>
                        <th>Total Cost</th>
                        <th>Total Sale</th>
                        <th>Profit</th>
                        <th>Margin %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.detalle.map((item, index) => (
                        <tr key={index}>
                          <td>{item.codigo}</td>
                          <td>{item.nombre}</td>
                          <td>
                            <span className="badge badge-secondary">{item.presentacion}</span>
                          </td>
                          <td>
                            <span className={`badge ${item.tipo === "Servicio" ? "badge-info" : "badge-primary"}`}>
                              {item.tipo === "Servicio" ? "Service" : "Product"}
                            </span>
                          </td>
                          <td className="text-center">{item.cantidad_vendida}</td>
                          <td className="text-center">
                            <span className="text-muted">{item.unidades_base}</span>
                          </td>
                          <td className="text-right">
                            {item.precio_compra_unitario ? formatCurrency(item.precio_compra_unitario) : "-"}
                          </td>
                          <td className="text-right">{formatCurrency(item.precio_venta)}</td>
                          <td className="text-right">{formatCurrency(item.costo_total)}</td>
                          <td className="text-right">{formatCurrency(item.venta_total)}</td>
                          <td className="text-right text-success font-bold">{formatCurrency(item.ganancia_neta)}</td>
                          <td className="text-center">
                            <span
                              className={`badge ${
                                item.margen_porcentaje >= 50
                                  ? "badge-success"
                                  : item.margen_porcentaje >= 20
                                  ? "badge-warning"
                                  : "badge-danger"
                              }`}
                            >
                              {item.margen_porcentaje.toFixed(2)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {!reportData && !loading && (
            <div className="no-data">
              <p>📭 Select a date range and generate the profit report</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}